/* eslint-disable react-refresh/only-export-components */
import {
  useCallback,
  useReducer,
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  ChevronDown,
  PanelLeftClose,
  PanelLeft,
  Menu,
  ChevronsUpDown,
  Check,
  Pin,
  PinOff,
  Search,
  Bell,
  Building2,
  Shield,
} from "lucide-react";
import {
  HomeIcon,
  CalendarIcon,
  PeopleIcon,
  HrIcon,
  FinanceIcon,
  MinistryIcon,
  OperationsIcon,
  MessagingIcon,
  ReportsIcon,
  ProfileIcon,
  SettingsIcon,
  DashboardIcon,
  ShieldIcon,
  DocumentIcon,
  ClipboardIcon,
  ChecklistIcon,
  IntegrationsIcon,
  HandshakeIcon,
  HeartIcon,
  BookIcon,
  CalendarCheckIcon,
  CareIcon,
  ChatIcon,
  MailIcon,
  MegaphoneIcon,
  NewsIcon,
  UsersIcon,
  CalendarSimpleIcon,
  DollarIcon,
} from "@/lib/phosphor-icons";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsTablet } from "@/hooks/use-tablet";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganization } from "@/hooks/useOrganization";
import { useTenantContext } from "@/hooks/useTenantContext";
import { useAccessContext } from "@/hooks/useAccessContext";
import { useActionTelemetry } from "@/hooks/useActionTelemetry";
import type { Module } from "@/lib/permissions";
import { useUiPreferences } from "@/hooks/useUiPreferences";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
// forgeLogo image replaced with styled amber gradient badge per design spec
import { useRecentlyViewed, type RecentItem } from "@/hooks/useRecentlyViewed";
import { useSidebarState } from "@/hooks/useSidebarState";
import { usePersonalizationPreferences } from "@/hooks/usePersonalizationPreferences";
import { FINANCE_IA_ITEMS } from "@/lib/finance-navigation";
import { careEntryInPlaceOfPeople } from "@/lib/care-placement";
import { navRoute, placeForNextChassis } from "@/lib/module-nav-routes";
import { useUiUplift } from "@/hooks/useUiUplift";
import { prefetchModuleEntryRoutes, isModuleEntryRoute } from "@/lib/lazy-routes";
import { getModuleAccent } from "@/lib/module-accents";
import { useModuleToggles } from "@/hooks/useModuleToggles";
import { useBillingStatus } from "@/hooks/usePlatformBilling";
import { useSchoolsEntitlement } from "@/features/schools/entitlement";
import {
  tierMeetsMinimum,
  MODULE_CONFIG,
  type ToggleableModule,
  type SubscriptionTier,
} from "@/lib/tier-config";

interface NavChild {
  label: string;
  href: string;
  icon?: React.ElementType;
  requiredModule?: Module;
  staffOnly?: boolean;
  /** Requires active-org owner/admin membership, not just platform super-admin access. */
  strictOrgAdminOnly?: boolean;
  /** Optional group key — children sharing the same group render under a collapsible header */
  group?: string;
  /** Tier-gated module — hide if tier is too low or admin toggled off */
  tierModule?: ToggleableModule;
  requiredSchoolAddon?: boolean;
}

interface NavItem {
  label: string;
  icon: React.ElementType;
  href?: string;
  color: string;
  children?: NavChild[];
  requiredModule?: Module;
  childVisibility?: 'any' | 'all';
  staffOnly?: boolean;
  /** Org-admin-only section — hidden for non-admins (writes behind these links
   *  are is_org_admin-gated server-side, so the audience is org admins). */
  adminOnly?: boolean;
  /** Prefix paths that should highlight this item even without children */
  matchPrefixes?: string[];
  /** Module ID for accent color lookup */
  moduleId?: string;
  /** Tier-gated module — hide entire section if tier is too low or admin toggled off */
  tierModule?: ToggleableModule;
  requiredSchoolAddon?: boolean;
}

interface VisualNavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  requiredModule?: Module;
  requiredAnyModule?: Module[];
  staffOnly?: boolean;
  adminOnly?: boolean;
  matchPrefixes?: string[];
  tierModule?: ToggleableModule;
  requiredSchoolAddon?: boolean;
}

/**
 * Forge Navigation IA - Restructured (Sprint 1)
 *
 * Dashboards module REMOVED — each dashboard absorbed into its parent module.
 * Calendar PROMOTED to position 2 (daily-driver feature).
 * HR, Finance, Member Portal sub-items GROUPED into collapsible categories.
 * Recently Viewed moved inside scrollable nav, collapsible (default collapsed).
 * Settings bottom-pinned.
 *
 * 1. Home - Direct link to Staff Today
 * 2. Calendar - Promoted (was position 9)
 * 3. People - Directory, People & Households, Engagement (absorbed from Dashboards)
 * 4. HR - Grouped: Overview | Workforce | Lifecycle | Admin
 * 5. Finance - Grouped: Core | Accounting | Settings
 * 6. Ministry - Services, Volunteers, Attendance, Kids Check-In
 * 7. Operations - Dashboard (absorbed from Dashboards), Care, Tasks, Projects, Assets
 * 8. Messaging - Hub, Templates, DMs, Announcements, Bulletins
 * 9. Reports - Standalone (to be distributed in Sprint 3)
 * 10. Member Portal - Grouped: My Info | My Church | My Giving | My Activity
 * ---
 * Bottom-pinned: Settings, Admin Portal, Collapse
 */
const financeIaById = Object.fromEntries(
  FINANCE_IA_ITEMS.map((item) => [item.id, item])
) as Record<(typeof FINANCE_IA_ITEMS)[number]["id"], (typeof FINANCE_IA_ITEMS)[number]>;

// Each entry's `href` and `matchPrefixes` come from src/lib/module-nav-routes.ts, which also decides
// the module accent of the pages the entry highlights under the next chassis (#7912).
export const navigation: NavItem[] = [
  // HOME - Direct link to Staff Today
  {
    label: "Home",
    icon: HomeIcon,
    ...navRoute("home"),
    color: "text-[#d49b0a]",
    moduleId: "home",
  },

  // CALENDAR - Single entry, SubLayout provides navigation to all sub-pages
  {
    label: "Calendar",
    icon: CalendarIcon,
    ...navRoute("calendar"),
    color: "text-[#0e7c7b]",
    moduleId: "calendar",
  },

  // PEOPLE - Single entry, SubLayout provides navigation to all sub-pages
  {
    label: "People",
    icon: PeopleIcon,
    ...navRoute("people"),
    color: "text-[#2563a8]",
    requiredModule: "people",
    moduleId: "people",
  },

  // HR - Single entry, hub page provides navigation to all sub-pages
  {
    label: "HR",
    icon: HrIcon,
    ...navRoute("hr"),
    color: "text-warning-400",
    requiredModule: "hr",
    tierModule: "hr_staff",
    moduleId: "hr",
  },

  // FINANCE - Single entry, hub page provides navigation to all sub-pages
  {
    label: "Finance",
    icon: FinanceIcon,
    ...navRoute("finance"),
    color: "text-success-400",
    requiredModule: "contributions",
    moduleId: "finance",
  },

  // SCHOOLS - school operations, billing, academics, and family workflows
  {
    label: "Schools",
    icon: BookIcon,
    ...navRoute("schools"),
    color: "text-[#0f766e]",
    requiredModule: "schools",
    requiredSchoolAddon: true,
    moduleId: "schools",
  },

  // MINISTRY - Single entry, SubLayout provides navigation to all sub-pages
  {
    label: "Ministry",
    icon: MinistryIcon,
    ...navRoute("ministry"),
    color: "text-accent-purple-400",
    moduleId: "ministry",
  },

  // OPERATIONS - Single entry, SubLayout provides navigation to all sub-pages
  {
    label: "Operations",
    icon: OperationsIcon,
    ...navRoute("projects"),
    color: "text-accent-purple-400",
    moduleId: "projects",
  },

  // MESSAGING - Single entry, hub page provides navigation to all sub-pages
  {
    label: "Messaging",
    icon: MessagingIcon,
    ...navRoute("messaging"),
    color: "text-[#0891b2]",
    requiredModule: "messaging",
    moduleId: "messaging",
  },

  // REPORTS - Standalone (to be distributed into parent modules in Sprint 3)
  {
    label: "Reports",
    icon: ReportsIcon,
    ...navRoute("reports"),
    color: "text-[#d0505a]",
    requiredModule: "reports",
    moduleId: "reports",
  },

  // MY PORTAL - Member self-service, grouped into categories
  {
    label: "Member Portal",
    icon: ProfileIcon,
    color: "text-[#8a6518]",
    moduleId: "memberPortal",
    children: [
      { label: "Overview", href: "/member", icon: DashboardIcon },
      // ─── My Info ───
      { label: "My Profile", href: "/member/profile", icon: ProfileIcon, group: "My Info" },
      { label: "My Household", href: "/member/household", icon: UsersIcon, group: "My Info" },
      { label: "Preferences", href: "/member/preferences", icon: SettingsIcon, group: "My Info" },
      { label: "Account Settings", href: "/member/settings", icon: SettingsIcon, group: "My Info" },
      // ─── My Church ───
      { label: "Member Directory", href: "/member/directory", icon: UsersIcon, group: "My Church" },
      { label: "Groups", href: "/member/groups", icon: UsersIcon, group: "My Church" },
      { label: "Events", href: "/member/events", icon: CalendarSimpleIcon, group: "My Church" },
      { label: "Weekly Bulletin", href: "/member/bulletin", icon: DocumentIcon, group: "My Church" },
      // ─── My Giving ───
      { label: "Giving History", href: "/member/giving", icon: DollarIcon, group: "My Giving" },
      { label: "Pledges", href: "/member/pledges", icon: HandshakeIcon, group: "My Giving" },
      { label: "Give Now", href: "/my/giving", icon: HeartIcon, group: "My Giving" },
      { label: "Sermons", href: "/my/sermons", icon: BookIcon, group: "My Giving" },
      // ─── My Activity ───
      { label: "Registrations", href: "/member/registrations", icon: CalendarCheckIcon, group: "My Activity" },
      { label: "Attendance", href: "/member/attendance", icon: CalendarCheckIcon, group: "My Activity" },
      { label: "Care Requests", href: "/member/requests", icon: CareIcon, group: "My Activity" },
      { label: "Notifications", href: "/member/notifications", icon: ChatIcon, group: "My Activity" },
    ],
  },
];

export const secondaryNav: NavItem[] = [
  {
    label: "Settings",
    icon: SettingsIcon,
    color: "text-sidebar-foreground/50",
    adminOnly: true, // L9: org-admin settings — was rendering for every member (phantom nav)
    // Settings owns its complete information architecture in SettingsLayout.
    // Keep the sidebar as a single entry point instead of a second, incomplete
    // menu that drifts from the route-level categories.
    ...navRoute("settings"),
  },
];

export const visualModeNavigation: VisualNavItem[] = [
  { label: "Home", href: "/today", icon: HomeIcon, matchPrefixes: ["/today"] },
  { label: "Calendar", href: "/events", icon: CalendarIcon, matchPrefixes: ["/events", "/calendar"] },
  { label: "People", href: "/people", icon: PeopleIcon, requiredModule: "people", matchPrefixes: ["/community", "/engagement", "/people"] },
  {
    label: "Finance",
    href: "/finance",
    icon: FinanceIcon,
    requiredAnyModule: ["contributions", "funds", "pledges"],
    matchPrefixes: ["/finance", "/contributions", "/pledges", "/funds"],
  },
  { label: "Operations", href: "/dashboard", icon: OperationsIcon, requiredModule: "projects", matchPrefixes: ["/dashboard", "/forms", "/documents", "/projects", "/tasks", "/assets"] },
  { label: "Requests", href: "/requests", icon: ClipboardIcon, requiredModule: "requests", matchPrefixes: ["/requests", "/operations/requests"] },
  { label: "HR", href: "/hr", icon: HrIcon, requiredModule: "hr", tierModule: "hr_staff", matchPrefixes: ["/hr"] },
  { label: "Settings", href: "/settings", icon: SettingsIcon, adminOnly: true, matchPrefixes: ["/settings"] },
];

const NAV_ITEM_ACTIVE_CLASS = "nav-item-active";
const NAV_SUB_ITEM_ACTIVE_CLASS = "nav-sub-item-active";

// Context for sidebar state
interface SidebarContextType {
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  isMobileOpen: boolean;
  setIsMobileOpen: (value: boolean) => void;
  isMobile: boolean;
  isTablet: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarUiState {
  isCollapsed: boolean;
  isMobileOpen: boolean;
}

type SidebarUiAction =
  | { type: "setCollapsed"; value: boolean }
  | { type: "setMobileOpen"; value: boolean };

const SIDEBAR_COLLAPSED_KEY = "sidebar:collapsed";

function getInitialCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

const initialSidebarUiState: SidebarUiState = {
  isCollapsed: getInitialCollapsed(),
  isMobileOpen: false,
};

const sidebarUiReducer = (state: SidebarUiState, action: SidebarUiAction): SidebarUiState => {
  switch (action.type) {
    case "setCollapsed":
      if (state.isCollapsed === action.value) {
        return state;
      }
      return { ...state, isCollapsed: action.value };
    case "setMobileOpen":
      if (state.isMobileOpen === action.value) {
        return state;
      }
      return { ...state, isMobileOpen: action.value };
    default:
      return state;
  }
};

const denyModuleAccess = () => false;

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }
  return context;
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(sidebarUiReducer, initialSidebarUiState);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const setIsCollapsed = useCallback((value: boolean) => {
    dispatch({ type: "setCollapsed", value });
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  const setIsMobileOpen = useCallback((value: boolean) => {
    dispatch({ type: "setMobileOpen", value });
  }, []);

  // Close mobile drawer when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile, setIsMobileOpen]);

  useEffect(() => {
    if (isTablet) {
      setIsCollapsed(true);
    }
  }, [isTablet, setIsCollapsed]);

  const isCollapsed = state.isCollapsed;
  const isMobileOpen = state.isMobileOpen;

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen, isMobile, isTablet }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen, isMobile } = useSidebar();
  const { data: accessContext, isLoading: accessLoading } = useAccessContext();
  const { moduleIdentity } = useUiPreferences();
  const { items: recentItems } = useRecentlyViewed();
  const { preferences, setPinnedPages } = usePersonalizationPreferences();
  // A user who pinned People before the /community -> /people rename has that
  // literal string in their persisted preferences.pinnedPages. Every read below
  // compares against this normalized array (never the raw preference) so an
  // old pin keeps matching the People item's current href, and any write
  // through setPinnedPages naturally re-persists the canonical value.
  const normalizedPinnedPages = useMemo(
    () => preferences.pinnedPages.map((href) => (href === "/community" ? "/people" : href)),
    [preferences.pinnedPages],
  );
  const { trackAction } = useActionTelemetry();
  // Nav-intent via event delegation: one handler on the sidebar container
  // captures every global-nav link click (React events bubble the React tree,
  // incl. through portals) without instrumenting ~10 NavLink render sites.
  // closest() returns null for non-link controls (toggles, pins) so they never
  // fire. logEvent auto-adds metadata.path (the origin page), so nav_click stays
  // distinct from the destination's mount-time page_view. Only the destination
  // href is logged — NOT textContent, which for recently-viewed/pinned record
  // links would be a member's name (PII into platform_events).
  const handleNavClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const anchor = (e.target as HTMLElement).closest('a[href^="/"]');
    if (!anchor) return;
    trackAction({
      action: "nav_click",
      metadata: { destination: anchor.getAttribute("href") },
      source: "global_nav",
    });
  }, [trackAction]);
  const isStaffMember = accessContext?.is_staff ?? false;
  const isPlatformAdmin = accessContext?.capabilities.isPlatformAdmin ?? false;
  const isStrictOrgAdmin = accessContext?.capabilities.isOrgAdmin ?? false;
  const isPlatformSuperAdmin = accessContext?.capabilities.isPlatformSuperAdmin ?? false;
  // Settings write routes allow active-org admins and platform super-admins.
  // Support/read-only platform roles stay in the Admin Portal rather than seeing
  // org settings links that their direct-route policy correctly denies.
  const isOrgAdmin =
    isStrictOrgAdmin || isPlatformSuperAdmin;
  // Support/read-only platform roles have no org Settings link (filtered out
  // above) and no other settings surface, so /admin is their Settings — label
  // it that way instead of "Admin Portal", which only applies when the org
  // Settings link is already visible (org admins, platform super-admins).
  const isPlatformOnlyAdmin = isPlatformAdmin && !isOrgAdmin;
  const adminPortalLabel = isPlatformOnlyAdmin ? "Settings" : "Admin Portal";
  const canAccessModule = accessContext?.capabilities.canAccessModule ?? denyModuleAccess;
  const permissionsLoading = accessLoading;

  // Tier + toggle gating for sidebar items
  const { isModuleEnabled, isLoading: togglesLoading } = useModuleToggles();
  const { effectiveTier } = useBillingStatus();
  const schoolsEntitlement = useSchoolsEntitlement();
  const currentTier = (effectiveTier || "free") as SubscriptionTier;
  const canAccessSchoolAddon = schoolsEntitlement.enabled;

  /** Returns true if a tier-gated module should be visible in the sidebar */
  const isTierModuleVisible = useCallback(
    (tierModule: ToggleableModule | undefined): boolean => {
      if (!tierModule) return true;
      const config = MODULE_CONFIG[tierModule];
      if (!tierMeetsMinimum(currentTier, config.minimumTier)) return false;
      return isModuleEnabled(tierModule);
    },
    [currentTier, isModuleEnabled],
  );

  // Under ui_uplift = next, Attendance is part of Calendar & Events, and the group list and
  // pastoral care part of People (MODULE_MAPS.md).
  const uiVariant = useUiUplift();
  const baseNavigation = useMemo(
    () => (uiVariant === "next"
      ? placeForNextChassis(navigation)
      : navigation),
    [uiVariant],
  );
  const baseVisualNavigation = useMemo(
    () => (uiVariant === "next"
      ? placeForNextChassis(visualModeNavigation)
      : visualModeNavigation),
    [uiVariant],
  );

  // Filter navigation items based on permissions + tier/toggles
  const filteredNavigation = useMemo(() => {
    if (permissionsLoading) return baseNavigation;

    return baseNavigation
      .map((baseItem) => {
        // Under next, someone without People still gets Care (see careEntryInPlaceOfPeople).
        const item = uiVariant === "next" ? careEntryInPlaceOfPeople(baseItem, canAccessModule("people")) : baseItem;
        if (item.staffOnly && !isStaffMember) {
          return null;
        }
        if (item.requiredSchoolAddon && !canAccessSchoolAddon) {
          return null;
        }
        // Tier gate check for the parent item
        if (!isTierModuleVisible(item.tierModule)) {
          return null;
        }
        if (item.children) {
          const visibleChildren = item.children.filter((child) => {
            if (child.staffOnly && !isStaffMember) return false;
            if (child.requiredSchoolAddon && !canAccessSchoolAddon) return false;
            if (!isTierModuleVisible(child.tierModule)) return false;
            if (!child.requiredModule) return true;
            return canAccessModule(child.requiredModule);
          });

          if (visibleChildren.length === 0) return null;

          return { ...item, children: visibleChildren };
        }

        if (item.requiredModule && !canAccessModule(item.requiredModule)) {
          return null;
        }

        return item;
      })
      .filter(Boolean) as NavItem[];
    // baseNavigation changes when the ui_uplift variant settles (Attendance and the group list
    // move module), so the filtered list follows it.
  }, [baseNavigation, canAccessModule, canAccessSchoolAddon, isStaffMember, permissionsLoading, isTierModuleVisible, uiVariant]);

  const filteredSecondaryNav = useMemo(() => {
    if (permissionsLoading) return secondaryNav;

    return secondaryNav
      .map((item) => {
        if (item.staffOnly && !isStaffMember) {
          return null;
        }
        if (item.requiredSchoolAddon && !canAccessSchoolAddon) {
          return null;
        }
        // L9: drop org-admin-only groups (e.g. Settings) for non-admins.
        if (item.adminOnly && !isOrgAdmin) {
          return null;
        }
        if (!isTierModuleVisible(item.tierModule)) {
          return null;
        }
        if (item.children) {
          const visibleChildren = item.children.filter((child) => {
            if (child.staffOnly && !isStaffMember) return false;
            if (child.strictOrgAdminOnly && !isStrictOrgAdmin) return false;
            if (child.requiredSchoolAddon && !canAccessSchoolAddon) return false;
            if (!isTierModuleVisible(child.tierModule)) return false;
            if (!child.requiredModule) return true;
            return canAccessModule(child.requiredModule);
          });

          if (visibleChildren.length === 0) return null;

          return { ...item, children: visibleChildren };
        }

        if (item.requiredModule && !canAccessModule(item.requiredModule)) {
          return null;
        }

        return item;
      })
      .filter(Boolean) as NavItem[];
  }, [canAccessModule, canAccessSchoolAddon, isStaffMember, isOrgAdmin, isStrictOrgAdmin, permissionsLoading, isTierModuleVisible]);

  const filteredVisualNavigation = useMemo(() => {
    if (permissionsLoading) return baseVisualNavigation;

    const items = uiVariant === "next"
      ? baseVisualNavigation.map((item) => careEntryInPlaceOfPeople(item, canAccessModule("people")))
      : baseVisualNavigation;
    return items.filter((item) => {
      if (item.staffOnly && !isStaffMember) return false;
      if (item.adminOnly && !isOrgAdmin) return false;
      if (item.requiredSchoolAddon && !canAccessSchoolAddon) return false;
      if (!isTierModuleVisible(item.tierModule)) return false;
      if (item.requiredAnyModule && !item.requiredAnyModule.some((module) => canAccessModule(module))) {
        return false;
      }
      if (!item.requiredModule) return true;
      return canAccessModule(item.requiredModule);
    });
  }, [baseVisualNavigation, canAccessModule, canAccessSchoolAddon, isOrgAdmin, isStaffMember, permissionsLoading, isTierModuleVisible, uiVariant]);

  const recentItemsForModules = useMemo(() => {
    if (permissionsLoading) return recentItems;
    const moduleByType: Record<RecentItem["type"], Module> = {
      person: "people",
      contribution: "contributions",
      request: "requests",
      project: "projects",
    };
    return recentItems.filter((item) => canAccessModule(moduleByType[item.type]));
  }, [canAccessModule, permissionsLoading, recentItems]);

  const recentItemIcons: Record<RecentItem["type"], React.ElementType> = {
    person: ProfileIcon,
    contribution: DollarIcon,
    request: CareIcon,
    project: OperationsIcon,
  };

  const [isRecentlyViewedOpen, setIsRecentlyViewedOpen] = useState(false);

  const currentPath = `${location?.pathname || ''}${location?.search || ''}`;

  const pinnableNavItems = useMemo(() => {
    const items: Array<{ label: string; href: string; icon?: React.ElementType }> = [];
    const collectItems = (navItems: NavItem[]) => {
      navItems.forEach((item) => {
        if (item.href) {
          items.push({ label: item.label, href: item.href, icon: item.icon });
        }
        if (item.children) {
          item.children.forEach((child) => {
            items.push({ label: child.label, href: child.href, icon: child.icon });
          });
        }
      });
    };
    collectItems(filteredNavigation);
    collectItems(filteredSecondaryNav);
    return items;
  }, [filteredNavigation, filteredSecondaryNav]);

  const pinnedNavItems = useMemo(() => {
    const lookup = new Map(pinnableNavItems.map((item) => [item.href, item]));
    return normalizedPinnedPages
      .map((href) => lookup.get(href))
      .filter(Boolean) as Array<{ label: string; href: string; icon?: React.ElementType }>;
  }, [pinnableNavItems, normalizedPinnedPages]);

  const togglePinnedPage = (href: string) => {
    setPinnedPages(
      normalizedPinnedPages.includes(href)
        ? normalizedPinnedPages.filter((item) => item !== href)
        : [...normalizedPinnedPages, href]
    );
  };

  const isActive = (href: string, matchPrefixes?: string[]) => {
    if (href.includes("?")) {
      return currentPath === href;
    }
    if (location?.pathname === href) return true;
    // For leaf nav items with matchPrefixes, also match sub-routes
    if (matchPrefixes?.length) {
      return matchPrefixes.some(
        (prefix) => location?.pathname === prefix || location?.pathname?.startsWith(prefix + "/"),
      );
    }
    return false;
  };

  const isChildActive = (href: string) => {
    if (href.includes("?")) {
      return currentPath === href;
    }
    return location?.pathname === href || location?.pathname?.startsWith(href + "/");
  };

  const isParentActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some((child) => isChildActive(child.href));
    }
    return false;
  };

  const routeEntryPrefetchProps = useCallback(
    (href: string) => {
      if (!isModuleEntryRoute(href)) {
        return {};
      }

      return {
        onMouseEnter: () => prefetchModuleEntryRoutes(href),
        onFocus: () => prefetchModuleEntryRoutes(href),
      };
    },
    [],
  );

  const handlePinnedPageClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, href: string) => {
      event.preventDefault();
      event.stopPropagation();
      setPinnedPages(
        normalizedPinnedPages.includes(href)
          ? normalizedPinnedPages.filter((item) => item !== href)
          : [...normalizedPinnedPages, href],
      );
    },
    [normalizedPinnedPages, setPinnedPages],
  );

  // Auto-expand ONLY when the route changes.
  // Otherwise, a manual toggle can appear to "open then instantly close" because the active-route
  // sync immediately overrides the user's click.
  const activeParentLabel = useMemo(() => {
    const isChildHrefActive = (href: string) => {
      if (href.includes("?")) return currentPath === href;
      return location?.pathname === href || location?.pathname?.startsWith(href + "/");
    };

    // Find best matching parent by picking the one with the longest matching child href
    // This prevents /finance (Dashboards) from shadowing /finance/queue (Finance)
    const findActiveParent = (items: NavItem[]) => {
      let bestLabel: string | null = null;
      let bestLen = -1;
      for (const item of items) {
        // Leaf items with matchPrefixes (e.g. Reports at /reports/*)
        if (!item.children && item.matchPrefixes && item.href) {
          const isLeafActive = item.matchPrefixes.some(
            (prefix) => location?.pathname?.startsWith(prefix + "/") || location?.pathname === prefix,
          );
          if (isLeafActive && item.href.length > bestLen) {
            bestLen = item.href.length;
            bestLabel = item.label;
          }
          continue;
        }
        if (!item.children) continue;
        for (const child of item.children) {
          if (isChildHrefActive(child.href) && child.href.length > bestLen) {
            bestLen = child.href.length;
            bestLabel = item.label;
          }
        }
      }
      return bestLabel;
    };

    return findActiveParent(filteredNavigation) ?? findActiveParent(filteredSecondaryNav);
  }, [currentPath, location?.pathname, filteredNavigation, filteredSecondaryNav]);

  const AUTO_EXPAND_DEBOUNCE_MS = 120;

  const { expandedItems, toggleExpand } = useSidebarState({
    activeParentLabel,
    currentPath,
    debounceMs: AUTO_EXPAND_DEBOUNCE_MS,
  });

  const handleParentToggle = useCallback(
    (label: string) => {
      toggleExpand(label);
    },
    [toggleExpand],
  );

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = item.href ? isActive(item.href, item.matchPrefixes) : isParentActive(item);
    const isExpanded = expandedItems.includes(item.label);
    const sectionId = `nav-section-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    const accent = item.moduleId ? getModuleAccent(item.moduleId) : null;
    const navIconTone = accent ? "" : "text-sidebar-foreground/70";
    const accentStyle = accent ? { color: accent.color } as React.CSSProperties : undefined;
    const activeAccentVars = accent ? {
      "--nav-accent": accent.color,
      "--nav-accent-tint": accent.tint,
      "--nav-accent-border": accent.border,
    } as React.CSSProperties : undefined;
    
    // Always show labels on mobile, even when sidebar is "collapsed" state on desktop
    const showLabels = !isCollapsed || isMobile;

    if (item.children) {
      return (
        <div key={item.label}>
          {isCollapsed && !isMobile ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <NavLink
                  to={item.children[0].href}
                  {...routeEntryPrefetchProps(item.children[0].href)}
                  // No aria-expanded/aria-controls here: in the collapsed rail
                  // this is a plain nav link (children show via the hover flyout,
                  // not a disclosure), and sectionId only exists in the expanded
                  // branch — so those attrs were a dangling/false ARIA relation.
                  className={cn(
                    "group text-meta flex w-full items-center justify-center rounded-xl p-3 transition-all duration-150",
                    active
                      ? NAV_ITEM_ACTIVE_CLASS
                      : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  )}
                  style={active ? activeAccentVars : undefined}
                >
                  <div className={cn("nav-icon-shell", navIconTone, active && "nav-icon-shell-active")} style={active ? activeAccentVars : accentStyle}>
                    <Icon
                      aria-hidden="true"
                      focusable="false"
                      className={cn("h-5 w-5 transition-all duration-200 group-hover:scale-110", navIconTone)}
                      style={active ? undefined : accentStyle}
                    />
                  </div>
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" className="flex flex-col gap-1 p-2">
                <span className="font-medium mb-1">{item.label}</span>
                {item.children.map((child) => (
                  <NavLink
                    key={child.href}
                    to={child.href}
                    {...routeEntryPrefetchProps(child.href)}
                    className={cn(
                      "text-meta py-1.5 px-2 rounded transition-colors flex items-center gap-2",
                      isChildActive(child.href) && NAV_SUB_ITEM_ACTIVE_CLASS
                    )}
                  >
                    {child.icon && <child.icon className="h-3.5 w-3.5" />}
                    {child.label}
                  </NavLink>
                ))}
              </TooltipContent>
            </Tooltip>
          ) : (
            <>
              {/*
               * Parent nav item with children renders as a button (toggle-only)
               * on BOTH desktop and mobile. Previously mobile wrapped this in a
               * NavLink that navigated to children[0].href AND toggled the
               * section, then the mobile sheet auto-closed on navigation —
               * which forced a second tap to actually pick a child. Now: tap
               * parent → expand in place, tap child → navigate. Single intent
               * per tap. Bug fixed: 2026-05-28.
               */}
              <button
                type="button"
                onClick={() => handleParentToggle(item.label)}
                aria-expanded={isExpanded}
                aria-controls={sectionId}
                className={cn(
                  "group text-meta flex w-full items-center justify-between rounded-xl px-3 py-2.5 font-medium transition-all duration-150",
                  active
                    ? NAV_ITEM_ACTIVE_CLASS
                    : "border-l-[3px] border-l-transparent text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                )}
                style={active ? activeAccentVars : undefined}
              >
                <span className="flex items-center gap-3">
                  <div className={cn("nav-icon-shell", navIconTone, active && "nav-icon-shell-active")} style={active ? activeAccentVars : accentStyle}>
                    <Icon
                      aria-hidden="true"
                      focusable="false"
                      className={cn("h-5 w-5 transition-all duration-200 group-hover:scale-110", navIconTone)}
                      style={active ? undefined : accentStyle}
                    />
                  </div>
                  <span className="transition-opacity duration-200">{item.label}</span>
                </span>
                <ChevronDown
                  aria-hidden="true"
                  focusable="false"
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    isExpanded && "rotate-180"
                  )}
                />
              </button>
              <div
                id={sectionId}
                role="region"
                aria-label={`${item.label} pages`}
                // `hidden` when collapsed so the clipped child links leave the
                // tab order AND the a11y tree together — the previous aria-hidden
                // alone left focusable links in a hidden subtree (WCAG 4.1.2 /
                // 2.4.3 failure). (`inert` would keep the slide animation but is
                // untyped in this @types/react; `hidden` is correct + typed.)
                hidden={!isExpanded}
                className={cn(
                  "overflow-hidden transition-all duration-300 ease-out",
                  isExpanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                )}
              >
                    <div className="ml-4 mt-1 space-y-0.5 py-1 border-l border-sidebar-border pl-4">
                  {(() => {
                    // Group children: ungrouped items render inline, grouped items render under a header
                    const elements: React.ReactNode[] = [];
                    const seenGroups = new Set<string>();

                    const renderChild = (child: NavChild) => {
                      const ChildIcon = child.icon;
                      const childActive = isChildActive(child.href);
                      const isPinned = normalizedPinnedPages.includes(child.href);
                      return (
                        <div key={child.href} className="group/child flex items-center gap-2">
                          <NavLink
                            to={child.href}
                            {...routeEntryPrefetchProps(child.href)}
                            className={cn(
                              "text-meta flex flex-1 items-center gap-2 rounded-lg px-3 py-2 transition-all duration-150",
                              childActive
                                ? NAV_SUB_ITEM_ACTIVE_CLASS
                                : "border-l-[2px] border-l-transparent text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                            )}
                          >
                            {ChildIcon && <ChildIcon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 group-hover/child:scale-110" />}
                            <span className="truncate">{child.label}</span>
                          </NavLink>
                          {!isCollapsed && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 transition group-hover/child:opacity-100"
                              onClick={(event) => handlePinnedPageClick(event, child.href)}
                              aria-label={isPinned ? "Unpin page" : "Pin page"}
                            >
                              {isPinned ? (
                                <Pin className="h-3.5 w-3.5 fill-warning text-warning" />
                              ) : (
                                <PinOff className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                        </div>
                      );
                    };

                    for (const child of item.children) {
                      if (!child.group) {
                        elements.push(renderChild(child));
                      } else if (!seenGroups.has(child.group)) {
                        seenGroups.add(child.group);
                        const groupChildren = item.children.filter((c) => c.group === child.group);
                        const groupHasActive = groupChildren.some((c) => isChildActive(c.href));
                        elements.push(
                          <div key={`group-${child.group}`} className="mt-1">
                            <span className={cn(
                              "block px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-200",
                              groupHasActive ? "text-sidebar-foreground/80" : "text-sidebar-foreground/40"
                            )}>
                              {child.group}
                            </span>
                            <div className="space-y-0.5">
                              {groupChildren.map(renderChild)}
                            </div>
                          </div>
                        );
                      }
                    }

                    return elements;
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      );
    }

    // Single item (no children)
    const isItemActive = isActive(item.href!, item.matchPrefixes);
    const linkContent = (
      <NavLink
        to={item.href!}
        {...routeEntryPrefetchProps(item.href!)}
        className={cn(
          "group text-meta flex items-center rounded-xl px-3 py-2.5 font-medium transition-all duration-150",
          showLabels ? "gap-3" : "justify-center",
          isItemActive
            ? NAV_ITEM_ACTIVE_CLASS
            : "border-l-[3px] border-l-transparent text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        )}
        style={isItemActive ? activeAccentVars : undefined}
      >
        <div className={cn("nav-icon-shell flex-shrink-0", navIconTone, isItemActive && "nav-icon-shell-active")} style={isItemActive ? activeAccentVars : accentStyle}>
          <Icon
            aria-hidden="true"
            focusable="false"
            className={cn("h-5 w-5 transition-all duration-200 group-hover:scale-110", navIconTone)}
            style={isItemActive ? undefined : accentStyle}
          />
        </div>
        {showLabels && (
          <span className="transition-opacity duration-200">{item.label}</span>
        )}
      </NavLink>
    );

    if (isCollapsed && !isMobile) {
      return (
        <Tooltip key={item.label} delayDuration={0}>
          <TooltipTrigger asChild>
            {linkContent}
          </TooltipTrigger>
          <TooltipContent side="right">
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    const isPinned = normalizedPinnedPages.includes(item.href!);

    return (
      <div key={item.label} className="group flex items-center gap-2">
        <div className="flex-1">{linkContent}</div>
        {showLabels && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 opacity-0 transition group-hover:opacity-100"
            onClick={(event) => handlePinnedPageClick(event, item.href!)}
            aria-label={isPinned ? "Unpin page" : "Pin page"}
          >
            {isPinned ? (
              <Pin className="h-3.5 w-3.5 fill-warning text-warning" />
            ) : (
              <PinOff className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>
    );
  };

  const visualModeEnabled = FEATURE_FLAGS.ui.moduleIdentity === "subtle";
  const isDesktopVisualMode = visualModeEnabled && !isCollapsed && !isMobile;

  const isVisualItemActive = (item: VisualNavItem) => {
    if (isChildActive(item.href)) return true;
    return (item.matchPrefixes ?? []).some((prefix) => location?.pathname?.startsWith(prefix));
  };

  const visualNavModuleMap: Record<string, string> = {
    Home: "home",
    Calendar: "calendar",
    People: "people",
    Finance: "finance",
    Operations: "projects",
    Requests: "projects",
    HR: "hr",
    Ministry: "ministry",
    Messaging: "messaging",
    Reports: "reports",
    "Member Portal": "memberPortal",
    Settings: "settings",
  };

  const renderVisualNavItem = (item: VisualNavItem) => {
    const Icon = item.icon;
    const active = isVisualItemActive(item);
    const moduleId = visualNavModuleMap[item.label];
    const accent = moduleId ? getModuleAccent(moduleId) : null;

    return (
      <NavLink
        key={item.label}
        to={item.href}
        {...routeEntryPrefetchProps(item.href)}
        className={cn(
          "group flex items-center gap-3.5 rounded-2xl px-3.5 py-2.5 text-[15px] font-medium transition-all duration-200",
          active
            ? "shadow-sm"
            : "text-sidebar-foreground/80 hover:bg-sidebar-accent/70 hover:text-sidebar-foreground"
        )}
        style={active && accent ? {
          backgroundColor: accent.tint,
          color: accent.foreground,
          boxShadow: `0 8px 20px -13px ${accent.color}40`,
        } : undefined}
      >
        <div
          className={cn(
            "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-all duration-200",
            active ? "" : "bg-sidebar-muted/45 group-hover:bg-sidebar-muted/65"
          )}
          style={accent ? (active ? {
            backgroundColor: accent.color,
            color: "white",
          } : {
            color: accent.color,
          }) : undefined}
        >
          <Icon aria-hidden="true" focusable="false" className="h-[18px] w-[18px] transition-transform duration-200 group-hover:scale-110" />
        </div>
        <span className="truncate tracking-[0.01em]">{item.label}</span>
      </NavLink>
    );
  };

  // Close mobile menu when navigating
  useEffect(() => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  }, [location?.pathname, isMobile, setIsMobileOpen]);

  const { organization, organizations, switchOrganization } = useOrganization();
  const { tenantContext, overrideContext } = useTenantContext();

  const handleOrganizationSwitch = useCallback(
    (event: MouseEvent<HTMLElement>, orgId: string) => {
      event.preventDefault();
      switchOrganization(orgId);
    },
    [switchOrganization],
  );
  
  // In support mode, show the overridden org context
  const displayOrgName = overrideContext?.organizationName ?? organization?.name;
  const displayOrgSlug = organization?.slug; // We only have slug from the user's own orgs
  const displayOrgLogo = organization?.logo_url;

  // On mobile, always show labels (never icon-only)
  const showLabels = !isCollapsed || isMobile;
  
  const sidebarContent = (
    // onClick delegation captures every NavLink click under this subtree (mobile
    // Sheet + desktop aside both render it). React synthetic events bubble the
    // React tree — including through Radix portals — so this single handler also
    // covers the collapsed-parent flyout links; they need no separate handler.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- delegation only: the real controls underneath are NavLinks/buttons that already handle keyboard activation, and Enter on a link fires this same click.
    <div className="flex h-full flex-col overflow-hidden" onClick={handleNavClick}>
      {/* Forge Logo — SVG cross-sword mark */}
      <div className={cn(
        "border-b border-sidebar-border transition-all duration-300 flex items-center",
        isCollapsed && !isMobile ? "p-2 justify-center" : "px-4 py-3 gap-3"
      )}>
        <img
          src="/forge-mark.svg"
          alt="Forge"
          className={cn(
            "flex-shrink-0 transition-all duration-300 drop-shadow-[0_2px_6px_rgba(232,160,32,0.2)]",
            isCollapsed && !isMobile ? "h-9 w-9" : "h-8 w-8"
          )}
          aria-hidden="true"
        />
        {!isCollapsed && !isMobile && (
          <span className="text-lg font-semibold text-sidebar-foreground tracking-tight">
            Forge
          </span>
        )}
      </div>

      {/* Organization switcher moved to top bar (AppLayout header) */}

      {/* Main Navigation */}
      <nav aria-label="Primary" className="flex-1 min-h-0 space-y-1 overflow-y-auto overscroll-contain px-3 py-4">
        {pinnedNavItems.length > 0 && (
          <div className="space-y-1 pb-3">
            {!isCollapsed && (
              <h3 className="px-2 sidebar-section-header font-bold tracking-widest">
                Pinned
              </h3>
            )}
            <div className="space-y-1">
              {pinnedNavItems.map((item) => {
                const ItemIcon = item.icon;
                const isPinned = normalizedPinnedPages.includes(item.href);
                const linkContent = (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    {...routeEntryPrefetchProps(item.href)}
                  className={cn(
                    "group text-meta flex items-center rounded-xl px-3 py-2 transition-all duration-150",
                    isCollapsed ? "justify-center" : "gap-3",
                    isChildActive(item.href)
                        ? NAV_ITEM_ACTIVE_CLASS
                        : "border-l-[3px] border-l-transparent text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  )}
                >
                    <div className={cn("nav-icon-shell flex-shrink-0", isChildActive(item.href) && "nav-icon-shell-active")}>
                      {ItemIcon && <ItemIcon aria-hidden="true" focusable="false" className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />}
                    </div>
                    {!isCollapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                );

                if (isCollapsed) {
                  return (
                    <Tooltip key={item.href} delayDuration={0}>
                      <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                      <TooltipContent side="right">{item.label}</TooltipContent>
                    </Tooltip>
                  );
                }

                return (
                  <div key={item.href} className="group flex items-center gap-2">
                    <div className="flex-1">{linkContent}</div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 transition group-hover:opacity-100"
                      onClick={(event) => handlePinnedPageClick(event, item.href)}
                      aria-label={isPinned ? "Unpin page" : "Pin page"}
                    >
                      {isPinned ? (
                        <Pin className="h-3.5 w-3.5 fill-warning text-warning" />
                      ) : (
                        <PinOff className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isDesktopVisualMode
          ? filteredVisualNavigation.map(renderVisualNavItem)
          : filteredNavigation.map(renderNavItem)}

        {/* Recently Viewed — now inside scrollable nav, collapsible (default collapsed) */}
        {recentItemsForModules.length > 0 && (
          <div className="mt-2 pt-2 border-t border-sidebar-border/50 space-y-1">
            {!isCollapsed ? (
              <button
                type="button"
                onClick={() => setIsRecentlyViewedOpen(!isRecentlyViewedOpen)}
                className="flex w-full items-center justify-between px-2 py-1 group"
              >
                <h3 className="sidebar-section-header font-bold tracking-widest">
                  Recently Viewed
                </h3>
                <ChevronDown
                  aria-hidden="true"
                  className={cn(
                    "h-3.5 w-3.5 text-sidebar-foreground/40 transition-transform duration-200 group-hover:text-sidebar-foreground/70",
                    isRecentlyViewedOpen && "rotate-180"
                  )}
                />
              </button>
            ) : null}
            <div
              className={cn(
                "overflow-hidden transition-all duration-200 ease-out",
                !isCollapsed && !isRecentlyViewedOpen ? "max-h-0 opacity-0" : "max-h-[300px] opacity-100"
              )}
            >
              <div className="space-y-0.5">
                {recentItemsForModules.map((item) => {
                  const ItemIcon = recentItemIcons[item.type];
                  const linkContent = (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      {...routeEntryPrefetchProps(item.path)}
                      className={cn(
                        "group text-meta flex items-center rounded-xl px-3 py-2 transition-all duration-150",
                        isCollapsed ? "justify-center" : "gap-3",
                        isChildActive(item.path)
                          ? NAV_ITEM_ACTIVE_CLASS
                          : "border-l-[3px] border-l-transparent text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      )}
                    >
                      <div className={cn("nav-icon-shell flex-shrink-0", isChildActive(item.path) && "nav-icon-shell-active")}>
                        <ItemIcon aria-hidden="true" focusable="false" className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                      </div>
                      {!isCollapsed && <span className="truncate">{item.name}</span>}
                    </NavLink>
                  );

                  if (isCollapsed) {
                    return (
                      <Tooltip key={item.id} delayDuration={0}>
                        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                        <TooltipContent side="right">{item.name}</TooltipContent>
                      </Tooltip>
                    );
                  }

                  return linkContent;
                })}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Bottom-pinned actions: Settings (gear icon) + Admin Portal + Collapse */}
      <div className="border-t border-sidebar-border px-3 py-2 space-y-1 flex-shrink-0">
        {/* Settings — gear icon with expandable flyout */}
        {filteredSecondaryNav.map(renderNavItem)}

        {/* Admin Portal Switcher - only for platform admins */}
        {isPlatformAdmin && (
          isCollapsed && !isMobile ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <NavLink
                  to="/admin"
                  aria-label={adminPortalLabel}
                  className="group flex w-full items-center justify-center rounded-xl p-3 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all duration-150"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-warning/20 transition-colors duration-200">
                    <Shield aria-hidden="true" focusable="false" className="h-5 w-5 text-warning transition-transform duration-200 group-hover:scale-110" />
                  </div>
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right">
                {adminPortalLabel}
              </TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/admin"
              className="group text-meta flex w-full items-center gap-3 rounded-xl px-3 py-2.5 font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-all duration-150"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-warning/20 transition-colors duration-200">
                <Shield aria-hidden="true" focusable="false" className="h-5 w-5 text-warning transition-transform duration-200 group-hover:scale-110" />
              </div>
              <span>{adminPortalLabel}</span>
            </NavLink>
          )
        )}

        {/* Collapse Toggle - only show on desktop */}
        {!isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "group w-full text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all duration-150",
              isCollapsed ? "justify-center px-0" : "justify-start gap-3"
            )}
          >
            {isCollapsed ? (
              <PanelLeft aria-hidden="true" focusable="false" className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
            ) : (
              <>
                <PanelLeftClose aria-hidden="true" focusable="false" className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  // Mobile: Use Sheet drawer
  if (isMobile) {
    return (
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-sidebar sidebar-gradient border-sidebar-border overflow-hidden flex flex-col">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation Menu</SheetTitle>
            <SheetDescription>
              Main app navigation with organization switcher, recent pages, and module links.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col h-full overflow-hidden">
            {sidebarContent}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Fixed sidebar
  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-sidebar sidebar-gradient border-r border-sidebar-border transition-all duration-300 ease-out hidden md:block overflow-hidden",
        isCollapsed ? "w-20" : "w-64"
      )}
    >
      {sidebarContent}
    </aside>
  );
}

// Mobile menu trigger button component
export function MobileMenuTrigger() {
  const { setIsMobileOpen, isMobile } = useSidebar();
  
  if (!isMobile) return null;
  
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setIsMobileOpen(true)}
      className="md:hidden"
      aria-label="Open menu"
      title="Open menu"
      data-sidebar-mobile-trigger
    >
      <Menu aria-hidden="true" focusable="false" className="h-5 w-5" />
      <span className="sr-only">Open menu</span>
    </Button>
  );
}
